// ui/presenters/modifierRuleRows.js
import { i18n } from 'boot/i18n';
import { toRaw } from 'vue';
import {
  degreeLevelOptions,
  qualifyingCredentialOptions,
} from 'src/contexts/staffCompetency';

import { CONDITION_FACTS, credentialConditionValue } from '../../domain/constants/modifierRule';
import { conditionOfKind } from '../../domain/services/modifierRules';

/**
 * Turns a payer's conditional modifier rows into what the editor renders.
 *
 * OUT HERE RATHER THAN IN THE COMPONENT, matching `codingRuleRows` next door: the component owns the
 * controls, this owns what a row says and what the dropdowns offer.
 *
 * THE VOCABULARY IS STAFF COMPETENCY'S, BORROWED THROUGH ITS PUBLISHED INTERFACE. Which credentials
 * exist, which bodies issue them and what a degree rung is called are that context's facts, not this
 * one's. Everything below maps them into the shape this editor's controls want and never restates
 * them — a hard-coded 'BCBA' here would be a second copy of a list that already has an owner.
 */

/**
 * The value standing for "this row does not ask about this".
 *
 * AN OPTION RATHER THAN A CLEAR BUTTON, because "any credential" is a positive statement a sheet
 * makes — a row keyed only to the degree is the common shape for an older Medicaid manual — and a
 * blank select reads as something nobody got round to filling in.
 */
export const ANY = '';

const anyOption = () => ({ value: ANY, label: i18n.t('settings.fundingSources.modifierRules.optionAny') });

/**
 * Every credential a row can ask for, as options, with the issuing body as the caption.
 *
 * THE BODY IS A CAPTION RATHER THAN A GROUP HEADING, because the list is short enough to read whole
 * and grouping it would make the common case — finding BCBA — a two-step scan through boards most
 * clinics never touch.
 */
export function credentialOptions() {
  return [anyOption()].concat(qualifyingCredentialOptions().map((credential) => ({
    value: credentialConditionValue(credential.organization, credential.level),
    label: credential.level,
    caption: credential.bodyName,
  })));
} // credentialOptions()

/** Every degree floor a row can ask for, as options, lowest rung first. */
export function degreeOptions() {
  return [anyOption()].concat(degreeLevelOptions().map((option) => ({
    value: option.value,
    label: i18n.t('settings.fundingSources.modifierRules.optionDegreeAtLeast', { level: option.label }),
  })));
} // degreeOptions()

/** The value the credential select shows for a row, or ANY when the row does not ask. */
export function credentialValueOf(row) {
  return conditionOfKind(row, CONDITION_FACTS.CREDENTIAL)?.value ?? ANY;
} // credentialValueOf()

/** The value the degree select shows for a row, or ANY when the row does not ask. */
export function degreeValueOf(row) {
  return conditionOfKind(row, CONDITION_FACTS.DEGREE_LEVEL)?.value ?? ANY;
} // degreeValueOf()

/**
 * A stable render key for a rule row, which the row itself cannot supply.
 *
 * A RULE IS A VALUE OBJECT WITH NO IDENTITY — two rows saying the same thing ARE the same row — and
 * that is deliberate in the model. A sortable list needs the opposite: a key that tracks one row
 * across a reorder, or the framework cannot tell which DOM node moved and the list snaps back to the
 * order it was in before the drag.
 *
 * SO THE KEY LIVES HERE RATHER THAN ON THE ROW. Minting an id into the stored shape would put a
 * rendering concern inside a value object and leave the mapper one more field to remember to strip.
 * Keyed by object identity instead, in a WeakMap, so a row that is dropped is collected with it.
 *
 * `toRaw` IS LOAD-BEARING. The draft is deeply reactive, so a row read out of it is a PROXY while the
 * replacement an editor builds is a plain object; the two are different references for the same row
 * and a map keyed on either alone would miss half the lookups.
 */
const rowKeys = new WeakMap();
let keySequence = 0;

export function ruleRowKey(row, index = 0) {
  const raw = toRaw(row);

  // A row this build cannot read is preserved as stored, and a primitive cannot key a WeakMap. It is
  // read-only, so its position is identity enough.
  if (raw === null || typeof raw !== 'object') {
    return `rule-unreadable-${index}`;
  }

  if (!rowKeys.has(raw)) {
    keySequence += 1;
    rowKeys.set(raw, `rule-${keySequence}`);
  }

  return rowKeys.get(raw);
} // ruleRowKey()

/**
 * Hands a replaced row the key of the row it replaces.
 *
 * EVERY EDIT BUILDS A NEW ROW OBJECT, because the draft is updated immutably. Without this, choosing
 * a credential or typing a modifier would mint a new key, Vue would treat it as a different row, and
 * the field being typed into would be torn down and rebuilt — losing focus on every keystroke.
 */
export function carryRuleRowKey(from, to) {
  const raw = toRaw(to);

  if (raw !== null && typeof raw === 'object') {
    rowKeys.set(raw, ruleRowKey(from));
  }

  return to;
} // carryRuleRowKey()
